import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../../api";

/**
 * Floating, draggable guidelines panel (no backdrop — the form behind stays usable).
 *
 * Views:
 *   LIST   — the matches as CRITERIA rows (ranked best-first), pick one to open it.
 *   DETAIL — the chosen guideline (CPT/ICD + sections), with a Back button.
 *   SEARCH — manual "Fetch by criteria" (Order Type → Order → Location → Technique, no payor).
 *
 * Window controls (next to ✕): minimize (collapse to header), maximize/restore.
 * CPT/ICD codes are copy-locked: only the copy icon copies — selection, Ctrl+C and
 * right-click are blocked.
 */
export function GuidelinesModal({ record, onClose, onUsed, initialCode, startSearch }) {
  const EDITABLE = ["orderType", "order", "anatomicalLocation", "technique"];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [fixed, setFixed] = useState({ client: "", specialty: "" });
  const [criteria, setCriteria] = useState({ orderType: "", order: "", anatomicalLocation: "", technique: "" });
  const [mode, setMode] = useState("results");     // "results" | "search"
  const [detailIdx, setDetailIdx] = useState(null); // null = list view; number = detail
  const [searched, setSearched] = useState(false);  // results came from a Fetch → show the criteria breadcrumb
  const [searchedCrit, setSearchedCrit] = useState(null);
  // The client/group this record is scoped to (from the server's AUTO response). Every manual search falls
  // back to this so it can never go out blank and match every client's guidelines.
  const recordClientRef = useRef("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    if (initialCode) {
      // Open straight to a specific guideline (the inherited one) by its 7-series code — no list.
      api.guidelineByCode(initialCode)
        .then((g) => {
          if (!alive) return;
          if (g) { setData({ guidelines: [g], options: {}, matchType: "code", count: 1 }); setDetailIdx(0); setMode("results"); setFixed({ client: g.clientName || "", specialty: g.specialtyName || "" }); recordClientRef.current = g.clientName || record.productionGroup || ""; }
          else setError("Guideline " + initialCode + " not found");
        })
        .catch((e) => alive && setError(e.message || "Failed to load guideline"))
        .finally(() => alive && setLoading(false));
      return () => { alive = false; };
    }
    api.recordGuidelines(record.id)
      .then((d) => {
        if (!alive) return;
        setData(d); setDetailIdx(null); setSearched(false);
        setMode(startSearch ? "search" : "results");
        const o = d?.options || {};
        const g0 = (d?.guidelines || [])[0] || {};
        // The record's client/group MUST drive the search. productionGroup is only set once a record is
        // "sent for production", so for most records it's blank — fall back to clinicGroup / derivedGroup
        // (the clinic's LACN/CCCMG bucket, set at import). Never let this end up blank, or a "Fetch by
        // criteria" would send an empty client and match EVERY client's guidelines (e.g. LACN showing on a
        // CCCMG record).
        const recGroup = d?.recordClient || record.productionGroup || "";
        recordClientRef.current = recGroup;
        const client = pick(o.clients, recGroup) || g0.clientName || recGroup || "";
        const specialty = (o.specialties && o.specialties.length === 1 ? o.specialties[0] : (g0.specialtyName || (o.specialties || [])[0] || ""));
        setFixed({ client, specialty });
      })
      .catch((e) => alive && setError(e.message || "Failed to load guidelines"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [record.id, record.productionGroup, record.orderName, initialCode, startSearch]);

  const runSearch = useCallback((crit, fx) => {
    setLoading(true); setError(null);
    // Safety net: never search with a blank client (would match every client's guidelines). Fall back to
    // the record's own group so a user only ever sees their record's client guidelines.
    const client = fx.client || recordClientRef.current || record.productionGroup || "";
    api.guidelinesSearch({ client, specialty: fx.specialty, ...crit })
      .then((d) => {
        setData(d); setMode("results");
        const gl = d?.guidelines || [];
        setDetailIdx(gl.length ? 0 : null);                // exact criteria → show the guideline straight away
        setSearched(true); setSearchedCrit({ ...crit });
      })
      .catch((e) => setError(e.message || "Search failed"))
      .finally(() => setLoading(false));
  }, []);

  // Narrow the cascade dropdowns without loading guideline bodies (optionsOnly=1) → fast on every change.
  const refreshOptions = useCallback((crit) => {
    setLoading(true); setError(null);
    api.guidelinesSearch({ client: fixed.client, specialty: fixed.specialty, ...crit, optionsOnly: 1 })
      .then((d) => setData((prev) => ({ ...(prev || {}), options: (d && d.options) || {} }))).catch(() => {}).finally(() => setLoading(false));
  }, [fixed]);

  function change(key, value) {
    const idx = EDITABLE.indexOf(key);
    const next = { ...criteria, [key]: value };
    for (let i = idx + 1; i < EDITABLE.length; i++) next[EDITABLE[i]] = "";
    setCriteria(next);
    refreshOptions(next);
  }


  // Records each copied guideline once (guidelineId = the 5-digit code) and tells the work view the code.
  const recorded = useRef(new Set());
  const commitUse = useCallback((code, g) => {
    if (code && !recorded.current.has(code)) {
      recorded.current.add(code);
      api.recordGuidelineUsage(record.id, {
        guidelineId: code, stage: record.sheet,
        clientName: g.clientName, specialtyName: g.specialtyName, orderTypeName: g.orderTypeName,
        orderName: g.orderName, anatomicalLocationName: g.anatomicalLocationName, techniqueName: g.techniqueName,
        status: g.status, cptCode: g.cptCode, cptDescription: g.cptDescription, icdCode: g.icdCode, icdDescription: g.icdDescription,
      }).catch(() => {});
    }
    if (onUsed) onUsed(code, g);
  }, [record.id, onUsed]);

  const opts = data?.options || {};
  const list = data?.guidelines || [];
  const current = detailIdx != null ? list[detailIdx] : null;

  return (
    <FloatingPanel onClose={onClose} title="Authorization Guidelines"
      subtitle={`${record.productionGroup || ""}${record.productionGroup ? "  ·  " : ""}${record.orderName || ""}`}>

      {mode === "search" ? (
        <SearchView
          fixed={fixed} criteria={criteria} opts={opts} loading={loading} canBack={!startSearch}
          onChange={change} onFetch={() => runSearch(criteria, fixed)}
          onBack={() => { setMode("results"); setDetailIdx(null); }}
        />
      ) : loading ? (
        <div className="glp-body"><div className="glp-center"><span className="glp-spin" /> Loading…</div></div>
      ) : error ? (
        <div className="glp-body"><div className="glp-note glp-warn">Couldn’t load guidelines: {error}</div></div>
      ) : current ? (
        <Detail g={current} onBack={() => (searched ? setMode("search") : setDetailIdx(null))} showBack={!initialCode && (searched || list.length > 1)} onUse={commitUse} />
      ) : list.length === 0 ? (
        <div className="glp-body">
          {searched && <Breadcrumb fixed={fixed} crit={searchedCrit} onEdit={() => setMode("search")} />}
          <div className="glp-notfound">
            <div className="glp-nf-icon">🔍</div>
            <div className="glp-nf-title">No guidelines found</div>
            <div className="glp-nf-sub">{searched ? "No guideline matches these criteria." : <>We couldn’t automatically match guidelines{record.orderName ? <> for <b>{record.orderName}</b></> : null}.</>}</div>
            <button className="glp-nf-btn pulse" onClick={() => setMode("search")}>{searched ? "Change criteria" : "Fetch by criteria"}</button>
          </div>
        </div>
      ) : (
        <>
          {searched && <Breadcrumb fixed={fixed} crit={searchedCrit} onEdit={() => setMode("search")} />}
          <ListView list={list} onPick={setDetailIdx} onSearch={() => setMode("search")} />
        </>
      )}
    </FloatingPanel>
  );
}

/* ------------------------------ LIST view ------------------------------ */
function ListView({ list, onPick, onSearch }) {
  return (
    <div className="glp-body">
      <div className="glp-listhead">
        {list.length === 1 ? "1 guideline found" : `${list.length} guidelines found`} — select one to view
      </div>
      <div className="glp-rows">
        {list.map((g, i) => {
          const path = [g.orderTypeName, g.orderName, g.anatomicalLocationName, g.techniqueName].filter(clean);
          return (
            <button key={g.id || i} className="glp-row" onClick={() => onPick(i)}>
              <span className={`glp-rowdot ${g.status === "PENDING" ? "p" : "a"}`} title={g.status === "PENDING" ? "Pending" : "Approved"} />
              <span className="glp-rowtext">
                {path.map((p, j) => (
                  <React.Fragment key={j}>
                    {j > 0 && <span className="glp-sep">›</span>}
                    <span className="glp-chip2">{p}</span>
                  </React.Fragment>
                ))}
              </span>
              <span className="glp-rowarr">›</span>
            </button>
          );
        })}
      </div>
      <div className="glp-listfoot">
        <button className="glp-link" onClick={onSearch}>Not the right one? Fetch by criteria</button>
      </div>
    </div>
  );
}

/* ----------------------------- one guideline ----------------------------- */
function Detail({ g, onBack, showBack, onUse }) {
  const pending = g.status === "PENDING";
  const crumb = [g.clientName, g.specialtyName, g.orderTypeName, g.orderName, g.anatomicalLocationName, g.techniqueName].filter(clean).join("  ›  ");
  const cpts = parseCpt(g.cptCode, g.cptDescription);
  const icds = parseIcd(g.icdCode, g.icdDescription);
  const sections = [
    ["generalGuidelines", "General", "#1E5A8E"],
    ["payorGuidelines", "Payor", "#7A5AF8"],
    ["facilityGuidelines", "Test Facility", "#C77700"],
    ["locationGuidelines", "Location / Clinic", "#0E7C66"],
    ["lcdGuidelines", "LCD", "#2563EB"],
  ].filter(([k]) => clean(g[k]));
  const block = (e) => e.preventDefault();  // copy-lock handler

  // 7-series code from Assist (fallback to 70000 + id). Copying it records usage via onUse.
  const code = g.code || (g.id != null ? String(70000 + Number(g.id)) : "—");
  const [copied, setCopied] = useState(false);
  const copyId = () => {
    const ok = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(ok).catch(() => fallbackCopy(code, ok));
    else fallbackCopy(code, ok);
    if (onUse) onUse(code, g);
  };

  return (
    <>
      <div className="glp-meta">
        <div className="glp-metatop">
          {showBack && <button className="glp-back" onClick={onBack}>‹ Back</button>}
          {pending
            ? <span className="glp-pending blink">● Pending Approval</span>
            : <span className="glp-approved">✓ Approved{(g.approvedByNames || g.approvedByName) ? ` · ${g.approvedByNames || g.approvedByName}` : ""}{g.approvedAt ? ` · ${fmtDate(g.approvedAt)}` : ""}</span>}
          <div className="glp-idbox" title="Copy this ID and paste it in the work form to complete">
            <span className="glp-idlabel">Guideline ID</span>
            <span className="glp-idcode">{code}</span>
            <button className="glp-idcopy" onClick={copyId}>{copied ? "✓ Copied" : "⧉ Copy"}</button>
          </div>
        </div>
        {crumb && <div className="glp-crumb" title={crumb}>{crumb}</div>}
      </div>

      <div className="glp-body">
        {/* CPT / ICD are copy-locked: block selection, Ctrl+C, cut and right-click — icon only. */}
        <div className="glp-codes glp-noselect" onCopy={block} onCut={block} onContextMenu={block}>
          <div className="glp-col">
            <div className="glp-h glp-h-cpt">CPT</div>
            <table className="glp-tbl">
              <thead><tr><th>Code</th><th className="glp-u">Units</th><th>Description</th></tr></thead>
              <tbody>
                {cpts.length ? cpts.map((r, i) => (
                  <tr key={i}>
                    <td className="glp-code glp-cpt">{r.code}{r.modifier ? <span className="glp-mod"> -{r.modifier}</span> : null}<CopyBtn text={r.code} /></td>
                    <td className="glp-u">{r.units && r.units !== "0" ? r.units : "—"}</td>
                    <td>{r.desc || "—"}</td>
                  </tr>
                )) : <tr><td className="glp-empty" colSpan={3}>No CPT codes</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="glp-col">
            <div className="glp-h glp-h-icd">ICD-10<span className="glp-icd-badge">⚠ These are strictly example ICDs. Please refer to MD notes for precise ICDs.</span></div>
            <table className="glp-tbl">
              <thead><tr><th>Code</th><th>Description</th></tr></thead>
              <tbody>
                {icds.length ? icds.map((r, i) => (
                  <tr key={i}><td className="glp-code glp-icd">{r.code}<CopyBtn text={r.code} /></td><td>{r.desc || "—"}</td></tr>
                )) : <tr><td className="glp-empty" colSpan={2}>No ICD-10 codes</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {sections.map(([k, label, color]) => (
          <div key={k} className="glp-section">
            <div className="glp-sec-head"><span className="glp-dot" style={{ background: color }} /><span className="glp-sec-title" style={{ color }}>{label}</span><span className="glp-rule" /></div>
            <div className="glp-rich" dangerouslySetInnerHTML={{ __html: g[k] }} />
          </div>
        ))}
      </div>
    </>
  );
}

/* ------------------------------ SEARCH view ------------------------------ */
/* breadcrumb of the fetched criteria — click a segment to edit from there (earlier selections kept) */
function Breadcrumb({ fixed, crit, onEdit }) {
  const parts = [];
  if (fixed.client) parts.push(fixed.client);
  if (fixed.specialty) parts.push(fixed.specialty);
  ["orderType", "order", "anatomicalLocation", "technique"].forEach((k) => { if (crit && crit[k]) parts.push(crit[k]); });
  return (
    <div className="glp-crumbbar">
      <span className="glp-crumb-path">{parts.join("  ›  ")}</span>
      <button className="glp-crumb-edit" onClick={onEdit} title="Change the criteria">Edit criteria</button>
    </div>
  );
}

function SearchView({ fixed, criteria, opts, loading, onChange, onFetch, onBack, canBack }) {
  // Fetch unlocks only once the user has drilled down as far as THIS order allows: Order Type + Order are
  // always required; Location / Technique are required only when the chosen order actually has those options
  // (if it has none, Fetch unlocks after Order). Stops "pick Order Type → Fetch" flooding unrelated guidelines.
  const hasLoc = Array.isArray(opts.anatomicalLocations) && opts.anatomicalLocations.length > 0;
  const hasTech = Array.isArray(opts.techniques) && opts.techniques.length > 0;
  const canFetch = !!criteria.orderType && !!criteria.order
    && (!hasLoc || !!criteria.anatomicalLocation)
    && (!hasTech || !!criteria.technique);
  const need = !criteria.orderType ? "Select an Order Type to begin."
    : !criteria.order ? "Now select an Order."
    : (hasLoc && !criteria.anatomicalLocation) ? "Select a Location."
    : (hasTech && !criteria.technique) ? "Select a Technique."
    : "Press Fetch to see the guideline.";
  // All cascade fields are always shown, but each stays LOCKED until the step before it is chosen — so the
  // Order box is disabled until an Order Type is picked, Location until an Order is picked, and Technique
  // after that. A field whose chosen order has no options of that kind (no locations / no techniques) stays
  // locked. Mirrors the cascade OrderType → Order → Location → Technique and the hint sequence above.
  const lockOrder = !criteria.orderType;
  const lockLoc = !criteria.order || !hasLoc;
  const lockTech = !criteria.order || !hasTech || (hasLoc && !criteria.anatomicalLocation);
  return (
    <>
      <div className="glp-crit-bar">
        {canBack && <button className="glp-back" onClick={onBack}>‹ Back</button>}
        <span className="glp-fixlbl" title={`${fixed.client} · ${fixed.specialty}`}>{(fixed.client || "—")}{fixed.specialty ? ` · ${fixed.specialty}` : ""}</span>
      </div>
      <div className="glp-body">
        <div className="glp-search-center">
          <div className="glp-search-title">Find a guideline by criteria</div>
          <div className="glp-search-grid">
            <Sel value={criteria.orderType} options={opts.orderTypes} ph="Order Type" onChange={(v) => onChange("orderType", v)} />
            <Sel value={criteria.order} options={opts.orders} ph="Order" onChange={(v) => onChange("order", v)} disabled={lockOrder} />
            <Sel value={criteria.anatomicalLocation} options={opts.anatomicalLocations} ph="Location" onChange={(v) => onChange("anatomicalLocation", v)} disabled={lockLoc} />
            <Sel value={criteria.technique} options={opts.techniques} ph="Technique" onChange={(v) => onChange("technique", v)} disabled={lockTech} />
          </div>
          <button className="glp-fetch glp-fetch-wide" onClick={onFetch} disabled={!canFetch || loading}
            style={(!canFetch || loading) ? { opacity: .5, cursor: "not-allowed" } : undefined}>Fetch</button>
          {loading
            ? <div className="glp-center" style={{ marginTop: 10 }}><span className="glp-spin" /> Loading…</div>
            : <div className="glp-hint" style={{ textAlign: "center", marginTop: 8 }}>{need}</div>}
        </div>
      </div>
    </>
  );
}

/* --------------------------- draggable shell + window controls --------------------------- */
function FloatingPanel({ title, subtitle, onClose, children }) {
  const W = 780;
  const [pos, setPos] = useState(() => ({ x: Math.max(16, (typeof window !== "undefined" ? window.innerWidth : 1200) - W - 28), y: 76 }));
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const drag = useRef(null);

  const onDown = (e) => {
    if (maximized) return;                 // don't drag while maximized
    const p = "touches" in e ? e.touches[0] : e;
    drag.current = { dx: p.clientX - pos.x, dy: p.clientY - pos.y };
    document.body.style.userSelect = "none";
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current) return;
      const p = "touches" in e ? e.touches[0] : e;
      setPos({ x: Math.min(Math.max(0, p.clientX - drag.current.dx), window.innerWidth - 140), y: Math.min(Math.max(0, p.clientY - drag.current.dy), window.innerHeight - 56) });
    };
    const up = () => { drag.current = null; document.body.style.userSelect = ""; };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false }); window.addEventListener("touchend", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up); };
  }, []);

  const style = minimized
    ? { left: 12, bottom: 84, top: "auto", width: 200, height: "auto", minWidth: 0, minHeight: 0 }   // compact pill by the sidebar, clear of the form
    : maximized
      ? { left: "3vw", top: "3vh", width: "94vw", height: "90vh" }
      : { left: pos.x, top: pos.y, width: W };                             // restored to where it was

  // Minimized → collapse to just the header bar, staying exactly where it was dragged.
  // Clicking the header (or the restore button) expands it back in place. Dragging is off while
  // minimized so a click always restores cleanly.
  return (
    <div className={`glp-panel ${minimized ? "min" : ""} ${maximized ? "max" : ""}`} style={style}>
      <div className="glp-head" onMouseDown={minimized ? undefined : onDown} onTouchStart={minimized ? undefined : onDown}
        onClick={minimized ? () => setMinimized(false) : undefined}
        style={minimized ? { cursor: "pointer" } : undefined}
        title={minimized ? "Click to expand" : undefined}>
        <div className="glp-head-txt">
          <div className="glp-title">Authorization Guidelines</div>
          {subtitle && <div className="glp-sub">{subtitle}</div>}
        </div>
        <div className="glp-winbtns">
          {!minimized && <button className="glp-win" title="Minimize" onClick={(e) => { e.stopPropagation(); setMinimized(true); }}>–</button>}
          <button className="glp-win" title={maximized ? "Restore" : "Maximize"} onClick={(e) => { e.stopPropagation(); setMaximized((m) => !m); setMinimized(false); }}>{maximized ? "❐" : "□"}</button>
          <button className="glp-win glp-winx" title="Close" onClick={(e) => { e.stopPropagation(); onClose(); }}>✕</button>
        </div>
      </div>
      {!minimized && children}
      <style>{CSS}</style>
    </div>
  );
}

/* -------------------------------- bits -------------------------------- */
function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    const t = String(text || "");
    const ok = () => { setDone(true); setTimeout(() => setDone(false), 1000); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(t).then(ok).catch(() => fallbackCopy(t, ok));
    else fallbackCopy(t, ok);
  };
  return <button className={`glp-copy ${done ? "done" : ""}`} onClick={copy} title={`Copy ${text}`}>{done ? "✓" : "⧉"}</button>;
}
function fallbackCopy(t, ok) {
  try { const ta = document.createElement("textarea"); ta.value = t; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); ok(); } catch { /* ignore */ }
}
// Searchable combobox. The list is rendered with position:fixed (anchored to the input) so it is NOT
// clipped by the panel's overflow:hidden — clicking the field shows the dropdown, and typing filters it.
function Sel({ value, options, ph, onChange, disabled }) {
  const list = Array.isArray(options) ? options : [];
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState(null);   // {left, top, width} of the fixed dropdown
  const ref = useRef(null);
  const inputRef = useRef(null);
  const place = () => { const r = inputRef.current && inputRef.current.getBoundingClientRect(); if (r) setPos({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 180) }); };
  const openList = () => { if (disabled) return; place(); setOpen(true); setQ(""); };
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(""); } };
    const reposition = () => place();
    document.addEventListener("mousedown", close);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => { document.removeEventListener("mousedown", close); window.removeEventListener("resize", reposition); window.removeEventListener("scroll", reposition, true); };
  }, [open]);
  const shown = q.trim() ? list.filter((o) => String(o).toLowerCase().includes(q.trim().toLowerCase())) : list;
  return (
    <div className="glp-combo" ref={ref}>
      <input ref={inputRef} className="glp-sel" placeholder={ph} disabled={disabled} readOnly={false}
        value={open ? q : (value || "")}
        onClick={openList}
        onFocus={openList}
        onChange={(e) => { setQ(e.target.value); if (!open) openList(); }} />
      <span className="glp-combo-caret" onMouseDown={(e) => { e.preventDefault(); open ? setOpen(false) : openList(); }}>▾</span>
      {value && !open && !disabled && (
        <button type="button" className="glp-combo-x" title="Clear" onMouseDown={(e) => { e.preventDefault(); onChange(""); }}>×</button>
      )}
      {open && pos && (
        <div className="glp-combo-list" style={{ position: "fixed", left: pos.left, top: pos.top, width: pos.width }}>
          {shown.length === 0
            ? <div className="glp-combo-empty">No matches</div>
            : shown.slice(0, 80).map((o) => (
              <div key={o} className={"glp-combo-item" + (o === value ? " on" : "")}
                onMouseDown={(e) => { e.preventDefault(); onChange(o); setOpen(false); setQ(""); }}>{o}</div>
            ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------ parsing ------------------------------ */
function parseCpt(cptStr, descStr) {
  if (!cptStr) return [];
  const codes = cptStr.split(", "); const descs = (descStr || "").split("; ");
  return codes.map((c, i) => { const p = c.split("-"); const cp = (p[0] || "").split("*"); return { code: (cp[0] || "").trim(), units: (cp[1] || "").trim(), modifier: (p[1] || "").trim(), desc: (descs[i] || "").trim() }; }).filter((x) => x.code);
}
function parseIcd(icdStr, descStr) {
  if (!icdStr) return [];
  const codes = icdStr.split(", "); const descs = (descStr || "").split("; ");
  return codes.map((c, i) => ({ code: c.trim(), desc: (descs[i] || "").trim() })).filter((x) => x.code);
}
function clean(s) { const t = (s == null ? "" : String(s)).trim(); return (!t || ["NA", "N/A"].includes(t.toUpperCase())) ? "" : t; }
function pick(list, token) {
  if (!Array.isArray(list) || !token) return "";
  const n = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const t = n(token);
  return list.find((o) => n(o) === t) || list.find((o) => n(o).includes(t)) || "";
}
function fmtDate(iso) { try { const d = new Date(iso); return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`; } catch { return ""; } }

const CSS = `
.glp-panel{position:fixed;z-index:4000;max-width:96vw;height:78vh;max-height:90vh;display:flex;flex-direction:column;
  background:var(--surface,#fff);border:1px solid var(--border-2,#d7d9ec);border-radius:var(--radius,14px);
  box-shadow:var(--shadow-lg,0 22px 60px rgba(15,46,69,.24));overflow:hidden;resize:both;min-width:380px;min-height:120px;
  font-size:13.5px;color:var(--ink,#1b1930)}
.glp-panel.min{height:auto !important;min-height:0 !important;min-width:0 !important;resize:none;overflow:hidden;width:200px !important;border-radius:12px;box-shadow:0 12px 30px rgba(15,23,42,.30)}

.glp-panel.min .glp-head{border-radius:12px;padding:9px 12px}

.glp-panel.min .glp-sub{display:none}

.glp-panel.min .glp-title{font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.glp-panel.max{resize:none}
.glp-head{display:flex;align-items:center;gap:10px;padding:10px 12px;cursor:move;color:#fff;
  background:var(--chrome-grad,linear-gradient(165deg,#102C49 0%,#163A5E 55%,#1E4E7E 100%))}
.glp-head-txt{flex:1;min-width:0}
.glp-title{font-weight:700;font-size:14.5px}
.glp-sub{font-size:12px;opacity:.85;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.glp-winbtns{display:flex;align-items:center;gap:5px;flex:none}
.glp-win{border:none;background:rgba(255,255,255,.16);color:#fff;width:28px;height:28px;border-radius:7px;font-size:13px;cursor:pointer;line-height:1}
.glp-win:hover{background:rgba(255,255,255,.32)}
.glp-winx:hover{background:#e5534b}
.glp-body{flex:1;overflow:auto;padding:14px 16px;display:flex;flex-direction:column;gap:16px}
.glp-center{display:flex;align-items:center;gap:10px;justify-content:center;color:var(--muted,#6b7191);padding:28px 0}
.glp-spin{width:16px;height:16px;border:2px solid var(--border-2,#d7d9ec);border-top-color:var(--brand,#2f80c4);border-radius:50%;display:inline-block;animation:glpspin .8s linear infinite}
@keyframes glpspin{to{transform:rotate(360deg)}}
.glp-note{border:1px solid var(--border,#e8e9f3);border-radius:10px;padding:16px;font-size:14px;background:var(--surface-2,#fafafe)}
.glp-warn{background:var(--warn-bg,#fdf2e2);border-color:#f3d79a;color:#7a5600}
.glp-dim{color:var(--muted,#6b7191);font-size:12.5px;margin-top:5px}
.glp-hint{color:var(--muted,#6b7191);font-size:13px;background:var(--surface-2,#fafafe);border:1px dashed var(--border-2,#d7d9ec);border-radius:8px;padding:14px 16px}
/* list view */
.glp-listhead{font-size:12.5px;font-weight:700;color:var(--muted,#6b7191);text-transform:uppercase;letter-spacing:.04em}
.glp-rows{display:flex;flex-direction:column;gap:8px}
.glp-row{display:flex;align-items:center;gap:10px;width:100%;text-align:left;background:var(--surface,#fff);
  border:1px solid var(--border,#e8e9f3);border-radius:10px;padding:11px 12px;cursor:pointer;font:inherit;color:inherit}
.glp-row:hover{border-color:var(--brand,#2f80c4);background:var(--brand-soft,#eaf3fb)}
.glp-rowdot{width:9px;height:9px;border-radius:50%;flex:none}
.glp-rowdot.a{background:var(--ok,#16a34a)} .glp-rowdot.p{background:var(--warn,#d97706)}
.glp-rowtext{flex:1;min-width:0;display:flex;flex-wrap:wrap;align-items:center;gap:5px}
.glp-chip2{font-size:12.5px;font-weight:600;color:var(--ink,#1b1930);background:var(--surface-2,#f3f5fb);border:1px solid var(--border,#e8e9f3);border-radius:6px;padding:2px 8px}
.glp-sep{color:var(--muted-2,#9aa0bc);font-weight:700}
.glp-rowarr{color:var(--muted-2,#9aa0bc);font-size:18px;font-weight:700;flex:none}
.glp-listfoot{display:flex;gap:14px;flex-wrap:wrap;align-items:center}
.glp-link-muted{background:transparent;color:var(--muted,#6b7191);text-decoration:underline}
.glp-nog{display:flex;flex-direction:column;gap:7px;max-width:520px}
.glp-nog-title{font-size:15px;font-weight:800;color:var(--ink,#1b1930)}
.glp-nog-hint{font-size:12.5px;color:var(--muted,#6b7191);margin-bottom:4px}
.glp-nog-l{font-size:12px;font-weight:700;color:var(--ink-2,#3d3a56);margin-top:4px}
.glp-req{color:var(--err,#dc2626)}
.glp-nog-in,.glp-nog-ta{border:1px solid var(--border-2,#d7d9ec);border-radius:8px;padding:9px 11px;font-size:14px;color:var(--ink,#1b1930);font-family:inherit;width:100%;box-sizing:border-box}
.glp-nog-ta{resize:vertical}
.glp-nog-err{font-size:12.5px;color:var(--err,#dc2626);font-weight:700}
.glp-nog-actions{display:flex;gap:10px;margin-top:10px}
/* meta bar (detail) */
.glp-meta{padding:10px 14px;border-bottom:1px solid var(--border,#e8e9f3);background:var(--surface,#fff);display:flex;flex-direction:column;gap:7px;flex:none}
.glp-metatop{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.glp-back{border:1px solid var(--border-2,#d7d9ec);background:var(--surface-2,#fafafe);color:var(--brand-strong,#1e5a8e);font-weight:700;font-size:12.5px;padding:5px 12px;border-radius:7px;cursor:pointer;flex:none}
.glp-back:hover{background:var(--brand-soft,#eaf3fb)}
.glp-idbox{margin-left:auto;display:flex;align-items:center;gap:8px;flex:none;background:var(--brand-soft,#eaf3fb);
  border:1px solid var(--border-2,#d7d9ec);border-radius:9px;padding:4px 6px 4px 11px}
.glp-idlabel{font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:var(--muted,#6b7191)}
.glp-idcode{font-size:16px;font-weight:800;letter-spacing:.12em;color:var(--brand-strong,#1e5a8e);font-family:ui-monospace,Menlo,Consolas,monospace}
.glp-idcopy{border:none;background:var(--brand,#2f80c4);color:#fff;font-weight:800;font-size:12px;padding:6px 11px;border-radius:7px;cursor:pointer}
.glp-idcopy:hover{background:var(--brand-strong,#1e5a8e)}
.glp-approved{font-size:13px;font-weight:800;color:var(--ok,#16a34a)}
.glp-pending{font-size:12px;font-weight:800;color:var(--warn,#d97706);background:var(--warn-bg,#fdf2e2);border:1px solid #f0cf93;padding:4px 10px;border-radius:4px}
.blink{animation:glpblink 1s ease-in-out infinite}
@keyframes glpblink{50%{opacity:.4}}
.glp-crumb{font-size:12px;font-weight:700;color:var(--brand-strong,#1e5a8e);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
  background:var(--brand-soft,#eaf3fb);border:1px solid var(--border-2,#d7d9ec);border-radius:8px;padding:6px 11px;max-width:100%}
/* criteria search row */
.glp-crit{display:flex;align-items:center;gap:6px;padding:9px 12px;border-bottom:1px solid var(--border,#e8e9f3);background:var(--brand-soft-2,#f2f8fd);flex-wrap:nowrap}
.glp-fixlbl{flex:none;font-size:12px;font-weight:800;color:var(--brand-strong,#1e5a8e);white-space:nowrap;max-width:140px;overflow:hidden;text-overflow:ellipsis}
.glp-sel{width:100%;padding:6px 34px 6px 8px;font-size:12.5px;border:1px solid var(--border-2,#d7d9ec);border-radius:7px;background:#fff;color:var(--ink,#1b1930);box-sizing:border-box;cursor:pointer}
.glp-sel:disabled{opacity:.5}
.glp-combo{position:relative;flex:1 1 0;min-width:0}
.glp-combo-caret{position:absolute;right:8px;top:50%;transform:translateY(-50%);color:var(--muted,#6b7191);font-size:11px;cursor:pointer;pointer-events:auto}
.glp-combo-x{position:absolute;right:24px;top:50%;transform:translateY(-50%);border:none;background:transparent;color:var(--muted,#6b7191);cursor:pointer;font-size:16px;line-height:1;padding:0 2px}
.glp-combo-list{z-index:4100;max-height:240px;overflow:auto;background:var(--surface,#fff);border:1px solid var(--border-2,#d7d9ec);border-radius:8px;box-shadow:0 12px 30px rgba(15,46,69,.24)}
.glp-combo-item{padding:7px 10px;font-size:12.5px;cursor:pointer;color:var(--ink,#1b1930);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.glp-combo-item:hover{background:var(--brand-soft,#eaf3fb)}
.glp-combo-item.on{background:var(--brand-soft,#eaf3fb);font-weight:700}
.glp-combo-empty{padding:8px 10px;font-size:12.5px;color:var(--muted,#6b7191)}
.glp-fetch{flex:none;border:none;background:var(--brand,#2f80c4);color:#fff;font-weight:700;font-size:12.5px;padding:7px 14px;border-radius:7px;cursor:pointer}
.glp-fetch:hover{background:var(--brand-strong,#1e5a8e)}
/* not found */
.glp-notfound{display:flex;flex-direction:column;align-items:center;text-align:center;gap:12px;padding:34px 20px}
.glp-nf-icon{font-size:30px;opacity:.75}
.glp-nf-title{font-size:16px;font-weight:800;color:var(--ink,#1b1930)}
.glp-nf-sub{font-size:13.5px;color:var(--muted,#6b7191);max-width:400px;line-height:1.5}
.glp-nf-btn{border:none;background:var(--brand,#2f80c4);color:#fff;font-weight:800;font-size:14px;padding:11px 24px;border-radius:10px;cursor:pointer;margin-top:2px}
.glp-nf-btn:hover{background:var(--brand-strong,#1e5a8e)}
.glp-nf-hint{font-size:12.5px;color:var(--warn,#d97706);background:var(--warn-bg,#fdf2e2);border:1px solid #f0cf93;border-radius:999px;padding:6px 14px;font-weight:700;margin-top:4px}
.pulse{animation:glppulse 1.5s ease-in-out infinite}
@keyframes glppulse{0%,100%{transform:scale(1);box-shadow:0 4px 14px rgba(47,128,196,.34)}50%{transform:scale(1.045);box-shadow:0 7px 22px rgba(47,128,196,.5)}}
.glp-link{border:none;background:var(--brand-soft,#eaf3fb);color:var(--brand-strong,#1e5a8e);font-weight:700;font-size:12.5px;padding:7px 13px;border-radius:7px;cursor:pointer}
/* codes */
.glp-noselect{user-select:none;-webkit-user-select:none;-ms-user-select:none}
.glp-codes{display:grid;grid-template-columns:repeat(auto-fit,minmax(270px,1fr));gap:16px}
.glp-col{min-width:0}
.glp-h{font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.glp-h-cpt{color:var(--brand,#2f80c4)} .glp-h-icd{color:#0E7C66}
.glp-icd-badge{font-size:10.5px;font-weight:800;color:var(--err,#dc2626);background:var(--err-bg,#fceceb);border:1px solid #f6c9c4;padding:2px 8px;border-radius:4px;letter-spacing:0;text-transform:none;animation:glpblink 1.1s ease-in-out infinite}
.glp-tbl{width:100%;border-collapse:collapse;font-size:13px;border:1px solid var(--border,#e8e9f3);border-radius:8px;overflow:hidden}
.glp-tbl th{background:var(--surface-2,#fafafe);text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:var(--muted,#6b7191);padding:6px 9px;border-bottom:1px solid var(--border,#e8e9f3)}
.glp-tbl td{padding:6px 9px;border-bottom:1px solid var(--border,#eef0f6);vertical-align:top}
.glp-tbl tr:last-child td{border-bottom:none}
.glp-tbl tr:nth-child(even) td{background:var(--surface-2,#fafbfd)}
.glp-u{width:1%;white-space:nowrap;text-align:center}
.glp-code{font-weight:700;white-space:nowrap;width:1%;font-family:ui-monospace,Menlo,Consolas,monospace}
.glp-cpt{color:var(--brand,#2f80c4)} .glp-icd{color:#0E7C66}
.glp-mod{color:#7A5AF8;font-weight:700}
.glp-empty{color:var(--muted-2,#9aa0bc);font-style:italic}
.glp-copy{margin-left:6px;border:none;background:transparent;color:var(--muted-2,#9aa0bc);cursor:pointer;font-size:12px;padding:1px 4px;border-radius:4px;line-height:1;vertical-align:middle;font-family:inherit;user-select:none}
.glp-copy:hover{color:var(--brand,#2f80c4);background:var(--brand-soft,#eaf3fb)}
.glp-copy.done{color:var(--ok,#16a34a)}
/* sections */
.glp-section{display:flex;flex-direction:column;gap:7px}
.glp-sec-head{display:flex;align-items:center;gap:9px}
.glp-dot{width:8px;height:8px;border-radius:50%;flex:none}
.glp-sec-title{font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;white-space:nowrap}
.glp-rule{flex:1;height:1px;background:var(--border,#e8e9f3)}
.glp-rich{font-size:13.5px;line-height:1.6;color:var(--ink-2,#3d3a56);padding-left:17px;word-break:break-word;overflow-wrap:anywhere}
.glp-rich ul,.glp-rich ol{margin:4px 0 4px 20px}
.glp-rich table{border-collapse:collapse;margin:4px 0}
.glp-rich td,.glp-rich th{border:1px solid var(--border,#e8e9f3);padding:4px 8px}
`;